import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Copy, Check, RefreshCw, ExternalLink, AlertCircle } from 'lucide-react';

const SitemapXML = () => {
  const [sitemapContent, setSitemapContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [blogPostCount, setBlogPostCount] = useState(0);
  const [lastGenerated, setLastGenerated] = useState(null);

  const generateSitemap = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Static URLs
      const staticUrls = [
        { url: '', priority: '1.0', changefreq: 'monthly' },
        { url: 'login', priority: '0.8', changefreq: 'monthly' },
        { url: 'register', priority: '0.8', changefreq: 'monthly' },
        { url: 'pricing', priority: '0.9', changefreq: 'monthly' },
        { url: 'about', priority: '0.7', changefreq: 'yearly' },
        { url: 'privacy', priority: '0.5', changefreq: 'yearly' },
        { url: 'terms', priority: '0.5', changefreq: 'yearly' },
        { url: 'blog', priority: '0.8', changefreq: 'weekly' },
      ];

      // Fetch published blog posts
      let blogPosts = [];
      
      try {
        const publishedQuery = query(
          collection(db, 'blog_posts'),
          where('status', '==', 'published'),
          orderBy('publishedAt', 'desc')
        );
        
        let blogSnapshot = await getDocs(publishedQuery);
        
        // If no published posts found, try looking for posts with published=true
        if (blogSnapshot.empty) {
          const publishedBoolQuery = query(
            collection(db, 'blog_posts'),
            where('published', '==', true)
          );
          blogSnapshot = await getDocs(publishedBoolQuery);
        }
        
        // If still no posts, get all posts
        if (blogSnapshot.empty) {
          const allPostsQuery = query(collection(db, 'blog_posts'));
          blogSnapshot = await getDocs(allPostsQuery);
        }
        
        if (!blogSnapshot.empty) {
          blogPosts = blogSnapshot.docs
            .map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                slug: data.slug || doc.id,
                updatedAt: data.updatedAt,
                publishedAt: data.publishedAt,
                status: data.status,
                published: data.published,
                publishDate: data.publishDate
              };
            })
            .filter(post => {
              const isPublished = post.status === 'published' || post.published === true;
              const isNotScheduledFuture = !post.publishDate || 
                (post.publishDate && new Date(post.publishDate.seconds ? post.publishDate.seconds * 1000 : post.publishDate) <= new Date());
              return isPublished && isNotScheduledFuture;
            });
        }
        
        setBlogPostCount(blogPosts.length);
      } catch (blogError) {
        console.error('Blog fetch error:', blogError);
        setBlogPostCount(0);
      }

      // Generate XML sitemap
      const baseUrl = 'https://coooly.co.uk';
      
      let sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">`;

      // Add static URLs
      staticUrls.forEach(({ url, priority, changefreq }) => {
        const fullUrl = url ? `${baseUrl}/${url}` : baseUrl;
        sitemap += `
  <url>
    <loc>${fullUrl}</loc>
    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>
    <changefreq>${changefreq}</changefreq>
    <priority>${priority}</priority>
  </url>`;
      });

      // Add blog post URLs
      if (blogPosts.length > 0) {
        blogPosts.forEach(post => {
          let lastmod = new Date().toISOString().split('T')[0];
          
          if (post.updatedAt) {
            if (post.updatedAt.toDate) {
              lastmod = post.updatedAt.toDate().toISOString().split('T')[0];
            } else if (post.updatedAt.seconds) {
              lastmod = new Date(post.updatedAt.seconds * 1000).toISOString().split('T')[0];
            }
          } else if (post.publishedAt) {
            if (post.publishedAt.toDate) {
              lastmod = post.publishedAt.toDate().toISOString().split('T')[0];
            } else if (post.publishedAt.seconds) {
              lastmod = new Date(post.publishedAt.seconds * 1000).toISOString().split('T')[0];
            }
          }
          
          sitemap += `
  <url>
    <loc>${baseUrl}/blog/${post.slug}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.6</priority>
  </url>`;
        });
      }

      // Add category URLs
      const categoryUrls = ['dental-hygiene', 'career-advice', 'business-tips', 'practice-management'];
      categoryUrls.forEach(category => {
        sitemap += `
  <url>
    <loc>${baseUrl}/blog/category/${category}</loc>
    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.7</priority>
  </url>`;
      });

      sitemap += `
</urlset>`;

      setSitemapContent(sitemap);
      setLastGenerated(new Date());
      setIsLoading(false);
      
    } catch (error) {
      console.error('Sitemap generation error:', error);
      const fallbackSitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>https://coooly.co.uk</loc>
    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>1.0</priority>
  </url>
  <url>
    <loc>https://coooly.co.uk/blog</loc>
    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>
</urlset>`;
      setSitemapContent(fallbackSitemap);
      setBlogPostCount(0);
      setLastGenerated(new Date());
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    generateSitemap();
  }, [generateSitemap]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(sitemapContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const totalUrls = 8 + blogPostCount + 4; // 8 static + blog posts + 4 categories

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Sitemap Generator</h1>
          <p className="text-gray-600 mt-2">
            Generate XML sitemap content including all published blog posts for search engine optimization.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-purple-100">
                <ExternalLink size={24} className="text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total URLs</p>
                <p className="text-2xl font-bold text-gray-900">{totalUrls}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-green-100">
                <Check size={24} className="text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Blog Posts</p>
                <p className="text-2xl font-bold text-gray-900">{blogPostCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-blue-100">
                <RefreshCw size={24} className="text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Last Generated</p>
                <p className="text-sm font-bold text-gray-900">
                  {lastGenerated ? lastGenerated.toLocaleTimeString() : 'Generating...'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Manual Update Process</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ol className="list-decimal list-inside space-y-1">
                  <li>Click "Copy XML Content" below to copy the generated sitemap</li>
                  <li>Open <code className="bg-yellow-100 px-1 rounded">public/sitemap.xml</code> in your code editor</li>
                  <li>Replace the entire content with the copied XML</li>
                  <li>Run <code className="bg-yellow-100 px-1 rounded">firebase deploy --only hosting</code> to update the live sitemap</li>
                  <li>Submit updated sitemap to Google Search Console if needed</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={copyToClipboard}
              disabled={isLoading || !sitemapContent}
              className="flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {copied ? (
                <>
                  <Check size={20} className="mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={20} className="mr-2" />
                  Copy XML Content
                </>
              )}
            </button>
            
            <button
              onClick={generateSitemap}
              disabled={isLoading}
              className="flex items-center justify-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw size={20} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Generating...' : 'Regenerate'}
            </button>
            
            <a
              href="/sitemap.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ExternalLink size={20} className="mr-2" />
              View Current Sitemap
            </a>
          </div>
        </div>

        {/* XML Content Display */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Generated XML Content</h2>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin h-8 w-8 text-gray-400" />
              <span className="ml-3 text-gray-600">Generating sitemap...</span>
            </div>
          ) : (
            <div className="relative">
              <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm font-mono border max-h-96 overflow-y-auto">
                <code className="text-black">{sitemapContent}</code>
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SitemapXML;
